import { canvasLabelMeasurer } from '@ardoq/graph-labeling';
import { colors } from '@ardoq/design-tokens';
import { ARDOQ_DEFAULT_FONT_FAMILY } from '@ardoq/typography';
import type { MeasureLabel } from './standard/toLeafText';
import { wrapWords } from './wrapWords';

/**
 * Smaller than the treemap's, because a circle and an arc have less room than a rectangle, and
 * above the `MINIMUM_READABLE_FONT_SIZE` of 6 that the graph views draw down to.
 */
export const SVG_LABEL_SIZE = 8;
export const SVG_LABEL_LINE_HEIGHT = 10;

/**
 * How wide a string is drawn at {@link SVG_LABEL_SIZE}. A canvas rather than a measured SVG
 * element: the two views ask this once per node per render, and the canvas measurer caches.
 */
export const measureSvgLabel = canvasLabelMeasurer(
  `400 ${SVG_LABEL_SIZE}px/${SVG_LABEL_LINE_HEIGHT}px ${ARDOQ_DEFAULT_FONT_FAMILY}`
);

/** Light on a dark shape, dark on a light one — the pair the component matrix resolves. */
export const toTextColor = (isDark: boolean): string =>
  isDark ? colors.textOnInvertedStrong : colors.textDefault;

/**
 * Half the chord across a circle of radius `r`, `distanceFromCenter` above or below its center —
 * how much room a line of text has at that height. The diameter is only available through the
 * middle, and using it everywhere is what let labels run outside their circle.
 */
export const halfChord = (r: number, distanceFromCenter: number): number => {
  const squared = r * r - distanceFromCenter * distanceFromCenter;
  return squared > 0 ? Math.sqrt(squared) : 0;
};

/** A line of text is drawn a quarter again as tall as it is set. */
export const toLineHeight = (labelSize: number): number =>
  Math.round(labelSize * 1.25);

/**
 * As large as a group's label is allowed to get, however much room its circle has.
 *
 * A heading is paid for out of its children: the deeper the band, the smaller everything inside.
 * On a three-level model the mean leaf radius runs 18.5 with no heading at all, 15.4 at 12px and
 * 12.2 at 22px — so the ceiling buys legibility for one name at the cost of every name below it.
 * 12px still reads as a heading against the 8px its leaves are set at.
 */
export const MAX_GROUP_LABEL_SIZE = 12;

/**
 * How much of a group's radius one line of its label is set at. A group the size of the pane
 * carries a heading; one the size of a leaf carries the same 8px its children do.
 */
const GROUP_LABEL_RADIUS_RATIO = 8;

/** How large a group writes its name, from the size of its circle. */
export const toGroupLabelSize = (r: number): number =>
  Math.round(
    Math.min(
      MAX_GROUP_LABEL_SIZE,
      Math.max(SVG_LABEL_SIZE, r / GROUP_LABEL_RADIUS_RATIO)
    )
  );

/**
 * How much of its radius a group may give up to its own name.
 *
 * Group headings and leaf names compete for the same pixels, and this is the dial between them.
 * Only the outermost groups are given a band this deep — see `HierarchicalPackingView` — because
 * the cost compounds with every level that takes one.
 */
export const MAX_BAND_SHARE_OF_RADIUS = 0.3;

/** Clear space between a label and the rim, so a glyph does not sit on the circle's edge. */
const LABEL_RIM_MARGIN = 2;

/**
 * The share of the padding it is given that `d3.pack` actually leaves between a parent's edge
 * and its children — 0.92 at 10px, 0.80 at 30px, 0.67 at 60px, measured against d3-hierarchy.
 * A band is requested inflated by this so the gap that materialises is the one that was wanted.
 */
export const PACK_PADDING_YIELD = 0.7;

/** How many lines a group may wrap its name over. */
const MAX_GROUP_LABEL_LINES = 2;

/**
 * The shallowest gap a group can say anything in: one line at the base size, and the clearance
 * that keeps it off the rim. A group inset by less than this goes unnamed, so the layout leaves
 * at least this much for the groups that are not given a heading.
 */
export const MIN_LABEL_BAND = SVG_LABEL_LINE_HEIGHT + LABEL_RIM_MARGIN;

/**
 * How far below the rim the top of a label has to sit for a line `lineWidth` across to fit.
 *
 * The chord at depth `m` is `2 * sqrt(2rm - m**2)`; setting that equal to the width and solving
 * gives `r - sqrt(r**2 - lineWidth**2 / 4)`. Measured at the top edge of the text and not its
 * centre line: near the rim the chord collapses fast, so a line that fits through its own middle
 * still has both ends outside the circle — which is what put the corners of `Supporting
 * Capabilities` above the curve while its middle sat inside.
 */
const toRimClearance = (r: number, lineWidth: number): number =>
  lineWidth >= 2 * r
    ? Infinity
    : Math.max(
        LABEL_RIM_MARGIN,
        r - Math.sqrt(r * r - (lineWidth * lineWidth) / 4)
      );

/**
 * How deep a band a circle of radius `r` needs to hold `lineCount` lines the widest of which is
 * `lineWidth` across: the clearance above the text plus the text itself.
 */
export const toLabelBand = ({
  r,
  lineWidth,
  lineCount,
  lineHeight,
}: {
  r: number;
  lineWidth: number;
  lineCount: number;
  lineHeight: number;
}): number => toRimClearance(r, lineWidth) + lineCount * lineHeight;

/**
 * How wide the topmost line of a block in `band` has to work with — the chord where the top of
 * the text sits, which is the narrowest point any part of it reaches.
 */
export const toTopLineWidth = ({
  r,
  band,
  lineCount,
  lineHeight,
}: {
  r: number;
  band: number;
  lineCount: number;
  lineHeight: number;
}): number => {
  const clearance = band - lineCount * lineHeight;
  return clearance < LABEL_RIM_MARGIN ? 0 : 2 * halfChord(r, r - clearance);
};

/**
 * How far above the circle's centre the middle of the text block sits. The block hangs from its
 * clearance below the rim rather than sitting centred in the band, so its top edge is where the
 * chord was measured.
 */
export const toBlockCenterOffset = ({
  r,
  band,
  lineCount,
  lineHeight,
}: {
  r: number;
  band: number;
  lineCount: number;
  lineHeight: number;
}): number =>
  r - (band - lineCount * lineHeight) - (lineCount * lineHeight) / 2;

type ToGroupLabelArgs = {
  label: string | null;
  /** The radius the circle came out at, which the band is then taken out of. */
  r: number;
  /** The deepest band there is room for: the gap the layout actually left above the children. */
  maxBand: number;
  measurerFor: (labelSize: number) => MeasureLabel;
};

export type GroupLabel = {
  labelSize: number;
  /** What the layout insets this group's children by, and where its own name goes. */
  band: number;
  /** The name as it will be drawn, already wrapped to the band that was reserved for it. */
  lines: string[];
};

const wrapsWhole = (lines: string[], label: string): boolean =>
  lines.length > 0 && lines.join(' ') === label.split(/\s+/).join(' ').trim();

/**
 * The size a group writes its name at, the band it needs, and the lines it breaks into — decided
 * together from the radius its circle came out at, so the room reserved and the name drawn in it
 * cannot disagree.
 *
 * A group takes the largest size its whole name fits at, over as many lines as it takes, rather
 * than the largest size its circle could carry: a name is what identifies the group, and
 * `Customer Relations` set small over two lines says more than `Customer Rel…` set large. Where
 * no size fits, the smallest is used against the deepest band allowed and the name is cut.
 */
export const toGroupLabel = ({
  label,
  r,
  maxBand,
  measurerFor,
}: ToGroupLabelArgs): GroupLabel => {
  const usableRadius = r;
  const largest = toGroupLabelSize(usableRadius);
  if (!label || usableRadius <= 0 || maxBand <= 0) {
    return { labelSize: largest, band: Math.max(0, maxBand), lines: [] };
  }
  const deepestBand = maxBand;
  const lineCounts = Array.from(
    { length: MAX_GROUP_LABEL_LINES },
    (_unused, index) => index + 1
  );

  const wrapAt = (labelSize: number, lineCount: number, band: number) =>
    wrapWords({
      text: label,
      availableWidth: toTopLineWidth({
        r: usableRadius,
        band,
        lineCount,
        lineHeight: toLineHeight(labelSize),
      }),
      measure: measurerFor(labelSize),
      maxLines: lineCount,
    });

  /* The band depends on how wide the lines are and the wrapping depends on how deep the band is,
     so the first guess is an even split of the name and the rest is refinement. */
  const fitAt = (labelSize: number, lineCount: number): GroupLabel | null => {
    const lineHeight = toLineHeight(labelSize);
    /* The text has to stand up in the gap as well as fit across it: its own height plus the
       clearance that buys it a wide enough chord. */
    if (lineCount * lineHeight + LABEL_RIM_MARGIN > deepestBand) {
      return null;
    }
    let lineWidth = measurerFor(labelSize)(label) / lineCount;
    for (let attempt = 0; attempt < 3; attempt++) {
      const band = toLabelBand({
        r: usableRadius,
        lineWidth,
        lineCount,
        lineHeight,
      });
      if (band > deepestBand) {
        return null;
      }
      const lines = wrapAt(labelSize, lineCount, band);
      if (wrapsWhole(lines, label)) {
        return { labelSize, band, lines };
      }
      lineWidth *= 1.25;
    }
    return null;
  };

  for (let labelSize = largest; labelSize >= SVG_LABEL_SIZE; labelSize--) {
    for (const lineCount of lineCounts) {
      const fitted = fitAt(labelSize, lineCount);
      if (fitted) {
        return fitted;
      }
    }
  }

  /* No size fits the name whole, so the smallest is set against every bit of gap there is and
     cut. Where the gap will not hold even one line, the group goes unnamed rather than writing
     over the children it was supposed to clear. */
  const labelSize = SVG_LABEL_SIZE;
  if (deepestBand < MIN_LABEL_BAND) {
    return { labelSize, band: deepestBand, lines: [] };
  }
  const cut = lineCounts
    .map(lineCount => wrapAt(labelSize, lineCount, deepestBand))
    .reduce(
      (best, lines) =>
        lines.join(' ').length > best.join(' ').length ? lines : best,
      [] as string[]
    );
  return { labelSize, band: deepestBand, lines: cut };
};

const measurers = new Map<number, MeasureLabel>();

/** A measurer per label size, kept because each one owns an offscreen canvas and a cache. */
export const measurerForSize = (labelSize: number): MeasureLabel => {
  const existing = measurers.get(labelSize);
  if (existing) {
    return existing;
  }
  const measurer = canvasLabelMeasurer(
    `400 ${labelSize}px/${toLineHeight(labelSize)}px ${ARDOQ_DEFAULT_FONT_FAMILY}`
  );
  measurers.set(labelSize, measurer);
  return measurer;
};
